import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  country_code: string | null;
  language: string;
  subscription_tier: string;
  created_at: string | null;
  updated_at: string | null;
}

interface AuthState {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, country: string) => Promise<void>;
  signOut: () => Promise<void>;
  loadProfile: () => Promise<void>;
  updateProfile: (profile: Partial<Profile>) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  loading: true,
  
  signIn: async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      set({ user: data.user });
      
      if (data.user) {
        await get().loadProfile();
      }
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  },
  
  signUp: async (email: string, password: string, country: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (error) throw error;
      
      set({ user: data.user });

      if (data.user) {
        // Create initial profile
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: data.user.id,
            email: data.user.email,
            country_code: country,
            language: 'en',
            subscription_tier: 'free',
          });

        if (profileError) throw profileError;

        // Create initial subscription
        const { error: subscriptionError } = await supabase
          .from('subscriptions')
          .insert({
            user_id: data.user.id,
            tier: 'free',
            status: 'active',
          });

        if (subscriptionError) throw subscriptionError;

        await get().loadProfile();
      }
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  },
  
  signOut: async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      set({ user: null, profile: null });
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  },
  
  loadProfile: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        set({ user: null, profile: null, loading: false });
        return;
      }
      
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (error) {
        console.error('Error loading profile:', error);
        set({ user: null, profile: null, loading: false });
        return;
      }

      set({ 
        user,
        profile,
        loading: false,
      });
    } catch (error) {
      console.error('Load profile error:', error);
      set({ user: null, profile: null, loading: false });
    }
  },

  updateProfile: async (profileData: Partial<Profile>) => {
    try {
      const { user } = get();
      if (!user) throw new Error('No user logged in');

      const { data: profile, error } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;
      set({ profile });
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  },
}));

// Initialize auth state
supabase.auth.onAuthStateChange(async (event, session) => {
  if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
    await useAuthStore.getState().loadProfile();
  } else if (event === 'SIGNED_OUT') {
    useAuthStore.setState({ user: null, profile: null, loading: false });
  }
});

// Initialize auth state on load
useAuthStore.getState().loadProfile();